/**
 * @fileoverview Generic functions that can be used by any tree.
 * @author Jeff Grossman (jeff@yext.com)
 */
goog.module('yext.storm.FolderTree');
goog.module.declareLegacyNamespace();

const ajax = goog.require('yext.ajax');
const yext = goog.require('yext');
const dialog = goog.require('yext.dialog');
const routes = goog.require('yext.storm.routes');
const helpers = goog.require('yext.location.helpers');

(function() {
  // Add hover class to all .tree-down-arrow since :hover pseudo-selector
  // doesn't work in IE7. Need to use live instead of delegate since trees in
  // dialogs are created dynamically.
  $(document).on('mouseenter mouseleave', '.dynatree-container .tree-down-arrow', function(e) {
    $(this).toggleClass('hover', e.type === 'mouseenter');
  });

  /**
   * Get the actual depth of a node, considering that the user may only be
   * seeing part of a tree. The root (business level folder) is depth 0.
   *
   * @param {DynaTreeNode} node
   * @return {number}
   */
  var getNodeDepth = function(node) {
    var depth = 0;
    var tempNode = node;
    while (tempNode && tempNode.parent && tempNode.parent.parent) {
      tempNode = tempNode.parent;
      depth++;
      if (tempNode.data.depth) {
        depth += tempNode.data.depth;
        break;
      }
    }
    return depth;
  };
  goog.exportSymbol('getNodeDepth', getNodeDepth);

  /**
   * Get the node that the menu is attached to.
   *
   * It is currently possible to have three tree down arrows visible at once
   * (one in the main tree for selected node, one for selected node in dialog
   * tree and one for hover in dialog tree). This makes it difficult to
   * know which node arrow was selected. Callers of this function should store
   * the 'node' as data in the menu when it is going to be shown.
   *
   * @return {DynaTreeNode | undefined}
   */
  var getNodeWithMenu = function() {
    if ($('.tree-down-arrow.hover').length == 1) {
      return $.ui.dynatree.getNode('.tree-down-arrow.hover');
    } else if ($('#folder-menu').is(':visible')) {
      return $('#folder-menu').data('node');
    } else if ($('#folder-menu-add-only').is(':visible')) {
      return $('#folder-menu-add-only').data('node');
    }
  };
  goog.exportSymbol('getNodeWithMenu', getNodeWithMenu);

  /**
   * Returns the least common ancestor of two arrays of numbers.
   * @param {!Array.<number>} node1
   * @param {!Array.<number>} node2
   * @return {number | null}
   */
  var leastCommonAncestor = function(node1, node2) {
    if (!node1 || node1.length === 0 || !node2 || node2.length === 0) {
      return null;
    }

    var index = Math.min(node1.length, node2.length) - 1;
    while (node1[index] != node2[index]) {
      if (--index < 0) {
        return null;
      }
    }
    return node1[index];
  };
  goog.exportSymbol('leastCommonAncestor', leastCommonAncestor);

  /**
   * Get the hierarchy of a folder as [grandparent, parent, child]
   *
   * @param {DynaTreeNode} node
   * @return {!Array.<number>}
   */
  var getFolderHierarchy = function(node) {
    var folderHierarchy = [];
    // "Root" nodes have a hidden dynatree root node, so they have a parent
    while (node && node.parent) {
      folderHierarchy.push(parseInt(node.data.key, 10));
      node = node.parent;
    }
    return folderHierarchy.reverse();
  };
  goog.exportSymbol('getFolderHierarchy', getFolderHierarchy);

  /**
   * Get the default dynatree fx. Enabled for everything except IE7.
   *
   * @return {!Object | null}
   */
  var defaultTreeFx = function() {
    if (!window.UserAgent.isIE7OrBelow()) {
      return {'height': 'toggle', 'duration': 200};
    }
    return null;
  };
  goog.exportSymbol('defaultTreeFx', defaultTreeFx);

  /**
   * Reload the tree, activating a new node and optionally expanding an
   * additional node by path.
   *
   * @param {DynaTree} tree
   * @param {string | undefined} activeNodePath
   * @param {string | undefined} expandedNodePath
   */
  var reloadTree = function(tree, activeNodePath, expandedNodePath) {
    // Remove animations for the reload, then restore them at the end. Fixes
    // a reload issue when adding a folder from the LocationEdit screen tree.
    var fx = $(tree.divTree).dynatree('option', 'fx');
    $(tree.divTree).dynatree('option', 'fx', null);

    // Reset the existing tree
    // dynatree always has a single hidden root (root.childList is topLevelFolders)
    $.each(tree.getRoot().childList, function(index, folder) {
      var node = tree.getNodeByKey(folder.data.key);
      if (node && node.isLazy()) {
        node.resetLazy();
      }
    });
    // Load the active node, then expand any extra nodes
    if (activeNodePath) {
      tree.loadKeyPath(activeNodePath, function(node, status) {
        if (status == 'loaded') {
          node.expand();
        } else if (status == 'ok') {
          node.activate();
          if (!expandedNodePath || activeNodePath == expandedNodePath
              || expandedNodePath.indexOf(activeNodePath) < 0) {
            node.expand();
          }

          // Load expandedNodePath after the active node is done loading
          if (expandedNodePath && activeNodePath != expandedNodePath) {
            tree.loadKeyPath(expandedNodePath, function(node, status) {
              if (status == 'loaded' || status == 'ok') {
                node.expand();
              }
            });
          }
        }
      });
    } else if (expandedNodePath) {
      tree.loadKeyPath(expandedNodePath, function(node, status) {
        if (status == 'loaded' || status == 'ok') {
          node.expand();
        }
      });
    }
    $(tree.divTree).dynatree('option', 'fx', fx);
  };
  goog.exportSymbol('reloadTree', reloadTree);

  /**
   * Update the state of the tree when an action is performed on a target node
   * (e.g., moved, add folder). Include sourceNodes if any nodes were moved.
   *
   * @param {DynaTree} tree
   * @param {DynaTreeNode} targetNode
   * @param {DynaTreeNode | Array.<DynaTreeNode> | undefined} sourceNodes
   */
  var nodeRefresh = function(tree, targetNode, sourceNodes) {
    if (!sourceNodes) {
      sourceNodes = [];
    } else if (!$.isArray(sourceNodes)) {
      sourceNodes = [sourceNodes];
    }
    if (!targetNode) {
      // No target node, due to it not being loaded in the tree (items moved
      // by move dialog)
      return;
    }
    var targetNodeKeyPath = getFolderHierarchy(targetNode).join('/');
    var activeNode = tree.getActiveNode();
    if (!activeNode) { // "All Locations" selected
      reloadTree(tree, null, targetNodeKeyPath);
    } else {
      var activeNodeKeyPath = getFolderHierarchy(activeNode).join('/');
      var sourceNodeIds = $.map(sourceNodes, function(node, index) {
        return node.data.key;
      });
      // activeNode may have just been moved
      if ($.inArray(activeNode.data.key, sourceNodeIds) >= 0) {
        var activeNodeHierarchy = getFolderHierarchy(targetNode);
        activeNodeHierarchy.push(activeNode.data.key);
        activeNodeKeyPath = activeNodeHierarchy.join('/');
      }
      reloadTree(tree, activeNodeKeyPath, targetNodeKeyPath);
    }
  };
  goog.exportSymbol('nodeRefresh', nodeRefresh);

  /**
   * Gets the default folder name to use when adding a child to a node. Tries
   * 'New Folder', 'New Folder 2', etc. until a name is not already used.
   *
   * @param {DynaTreeNode | undefined} node
   * @return {string}
   */
  var newFolderDefaultName = function(node) {
    var defaultName = yext.msg('New Folder');
    if (!node) {
      return defaultName;
    }
    var defaultNameIndex = 1;
    var childContainsName = function(folder, name) {
      if (folder && folder.childList) {
        var contains = false;
        $.each(folder.childList, function(index, child) {
          if (child.data.title == name) {
            contains = true;
            return false;
          }
        });
        return contains;
      }
      return false;
    };
    var tempName = defaultName;
    while (childContainsName(node, tempName)) {
      tempName = defaultName + ' ' + (++defaultNameIndex);
    }
    return tempName;
  };
  goog.exportSymbol('newFolderDefaultName', newFolderDefaultName);

  var getTreeNodeTitleWidth = function(node, nodeSpan) {
    var $title = $(nodeSpan).children('.dynatree-title');
    var $text = $title.children('span:first-child');
    var $count = $title.children('.dynatree-count');
    var fontFamily = $text.css('font-family');
    var fontSize = $text.css('font-size');
    var fontWeight = $text.css('font-weight');

    var elem = document.createElement('canvas');
    var ctxt;
    if (elem && elem.getContext && elem.getContext('2d')) { // canvas support
      ctxt = elem.getContext('2d');
      ctxt.font = [fontWeight, fontSize, fontFamily].join(' ');
    }

    var title = node.data.title;

    if (ctxt) { // html5 canvas
      return ctxt.measureText(title).width;
    } else { // offsetWidth fallback
      var $span = $('<span>');
      $span.css('font-family', fontFamily);
      $span.css('font-size', fontSize);
      $span.css('font-weight', fontWeight);
      $span.text(title);
      $title.append($span);
      var width = Math.max($span[0].offsetWidth, $span.width());
      $span.remove();
      return width;
    }
  };
  goog.exportSymbol('getTreeNodeTitleWidth', getTreeNodeTitleWidth);

  /**
   * Handling folder add action for the simple tree.
   * @suppress {deprecated}
   */
  var onFolderAdd_ = function(e, node, $tree) {
    $('.dynatree-container .folder-add-dialog').remove();
    var addDialog = $('.folder-add-dialog').clone();
    addDialog.find('input.folder-add-name').val(newFolderDefaultName(node));
    addDialog.find('.cancel').click(function(e) {
      addDialog.remove();
      return false;
    });

    var folderAddError = $.proxy(function(message) {
      dialog.hideProgressDialog();
      addDialog.remove();
      if (!message) {
        message = yext.msg('Error adding folder.');
      }
      dialog.showErrorDialog(message);
    }, this);
    addDialog.find('.primary').click(function(e) {
      var folderName = addDialog.find('input.folder-add-name').val();
      if (folderName && folderName.length > 0) {
        $.ajax({
          'url': routes.Folders.add(),
          'data': {
            'folderId': node.data.key,
            'name': folderName,
          },
          'type': 'POST',
          'success': $.proxy(function(result) {
            if (result && result.result === 'success') {
              addDialog.remove();
              nodeRefresh($tree.dynatree('getTree'), node, []);
            } else {
              folderAddError((result || {}).reason);
            }
          }, this),
          'error': ajax.errorWrapper(function() {
            folderAddError();
          }),
        });
      }
      return false;
    });
    addDialog.find('input.folder-add-name').keypress(function(e) {
      if (e.which == $.ui.keyCode.ENTER) {
        addDialog.find('.primary').click();
        return false;
      }
    });

    var hideAddDialog = function(e) {
      addDialog.remove();
    };
    $(document).click(hideAddDialog);
    $(document).keydown(function(e) {
      if (e.keyCode == $.ui.keyCode.ESCAPE) {
        hideAddDialog(e);
      }
    });

    addDialog.show();
    $(node.span).after(addDialog);
    addDialog.find('input.folder-add-name').focus().select();
  };

  /**
   * A simple tree (i.e., most trees other than the fancy sidebar one on the
   * locations screen). Only supported action is add folder (no edit, rename,
   * or delete).
   * @suppress {deprecated}
   */
  $.fn.simpleFolderTree = function(options) {
    if (options === 'destroy') {
      this.each(function() {
        $(this).dynatree('destroy');
      });
      $('#folder-menu-add-only').hide();
      $('#folder-menu-add-only .folder-menu-items .add').unbind('.simpleFolderTree');
      return;
    }

    var settings = $.extend({
      'title': yext.msg('Folder Tree'),
      'topLevelFolders': [],
      'initialKeyPath': '',
      'manageFoldersPermission': false,
      'url': routes.Folders.list(),
      'data': {},
      'idPrefix': 'tree-id-',
      'onActivate': null,
    }, options);

    if (this.data('simpleFolderTree')) {
      this.simpleFolderTree('destroy');
    }

    return this.each(function() {
      $(this).dynatree({
        'title': settings['title'],
        'minExpandLevel': 1,
        'fx': window.defaultTreeFx(),
        'autoFocus': false,
        'generateIds': true,
        'idPrefix': settings['idPrefix'],
        'selectMode': 1,
        'children': settings['topLevelFolders'],
        'onPostInit': function(isReloading, isError) {
          if (!isReloading) {
            if (settings['initialKeyPath'].length > 0) {
              this.loadKeyPath(settings['initialKeyPath'], function(node, status) {
                if (status == 'loaded') {
                  node.expand();
                } else if (status == 'ok') {
                  node.activate();
                  node.expand();
                }
              });
            } else if (settings['topLevelFolders'].length == 1) {
              // nothing to restore, but expand the top level folder
              this.getNodeByKey(settings['topLevelFolders'][0].key).expand();
            }
            $(this.divTree).unbind('keydown.dynatree keypress.dynatree');
            $(this.divTree).show();
          }
        },
        'onActivate': function(node) {
          // $('input[name="folderId"]').val(node.data.key);
          if (getNodeDepth(node) < 5) {
            $(node.span).find('.tree-down-arrow').addClass('activated').show();
          }
          if (settings['onActivate']) {
            $.proxy(settings['onActivate'], this)(node);
          }
        },
        'onCreate': function() {
          if (typeof(settings['onCreate']) == 'function') {
            settings['onCreate']();
          }
        },
        'onDeactivate': function(node) {
          $(node.span).find('.tree-down-arrow').removeClass('activated').hide();
        },
        'onClick': function(node, event) {
          if ($(event.target).hasClass('tree-down-arrow')
              || $(event.target).hasClass('folder-add-dialog')
              || $(event.target).parents().hasClass('folder-add-dialog')) {
            return false;
          }
          // When selecting an expanded node, don't collapse the node if clicking
          // on the link or folder icon (not clicking on [-])
          if (!node.isActive() && node.isExpanded() && !$(event.target).is('.dynatree-expander')) {
            node.activate();
            return false;
          }
          return true;
        },
        'onCustomRender': function(node) {
          var html = '<a href="#" class="dynatree-title"><span style="float: left">' + helpers.escapeInput(node.data.title) + '</span>';
          if (settings['manageFoldersPermission']) {
            if (node.isActive()) {
              html += ' <span class="tree-down-arrow activated"></span>';
            } else {
              html += ' <span class="tree-down-arrow" style="display: none; "></span>';
            }
          }
          html += '</a>';
          return html;
        },
        'onLazyRead': function(node) {
          node.appendAjax({
            'url': settings['url'],
            'data': $.extend({'folderId': node.data.key, 'permissionId': node.data['permissionId']}, settings['data']),
          });
        },
      });

      // Show down arrow when hovering on tree row.
      $(this).undelegate('a', 'hover.foldertree');
      $(this).delegate('a', 'hover.foldertree', function(e) {
        var node = $.ui.dynatree['getNode'](this);
        if (getNodeDepth(node) < 5) {
          if (e.type === 'mouseenter') {
            if (!$('#folder-menu-add-only').is(':visible')) {
              $(this).children('.tree-down-arrow').show();
            }
          } else {
            if (!$('#folder-menu-add-only').is(':visible') && !$(this).children('.tree-down-arrow').hasClass('activated')) {
              $(this).children('.tree-down-arrow').hide();
            }
          }
        }
      });

      // Show menu when clicking on down arrow.
      $(this).undelegate('.tree-down-arrow', 'click.foldertree');
      $(this).delegate('.tree-down-arrow', 'click.foldertree', function(e) {
        var menu = $('#folder-menu-add-only');
        menu.css('z-index', 2000); // needs to be this high for location move dialog
        menu.show();

        var node = getNodeWithMenu();
        menu.data('node', node);

        var add = menu.find('.add');
        if (getNodeDepth(node) >= 5) {
          add.hide();
        } else {
          add.show();
        }
        menu.position({
          'of': $(this),
          'my': 'right top',
          'at': 'right bottom',
        });

        return false;
      });

      var $this = $(this);
      // Hide menu on hitting escape or clicking in document.
      var hideMenu = function(e) {
        $this.find('#folder-menu-add-only').hide();
        $this.find('.tree-down-arrow:not(.activated)').hide();
      };
      $(document).unbind('.hideAddMenu');
      $(document).bind('click.hideAddMenu', hideMenu);
      $(document).bind('keydown.hideAddMenu', function(e) {
        if (e.keyCode == $.ui.keyCode.ESCAPE) {
          hideMenu(e);
        }
      });
      $(this).unbind('scroll.foldertree');
      $(this).bind('scroll.foldertree', hideMenu);

      $('#folder-menu-add-only .folder-menu-items .add').bind(
        'click.simpleFolderTree',
        $.proxy(function(e) {
          onFolderAdd_(e, getNodeWithMenu(), $(this));
          hideMenu();
          return false;
        }, this),
      );
    });
  };
})();

